import type { FAQ } from './types';

/**
 * Customer-audience FAQs. Covers the 80% case of "I'm a customer
 * tenant trying to use the platform". Reseller / platform-admin
 * specific questions live in separate files (sprint 2).
 *
 * Style guide:
 * - Question is what a real user types into search ("¿por qué...?",
 *   "¿cómo...?"), not a doc-style title.
 * - Answer is short, decisive. Link to the portal page that does the
 *   thing whenever possible (Link component, not a deep-link string).
 * - Tags catch synonyms the user might search but the question text
 *   doesn't include (e.g. "10dlc", "tcpa", "shortcode").
 *
 * Keep entries < ~5 lines of body text. If you find yourself writing
 * paragraphs, split into multiple FAQs that link to each other.
 */
export const CUSTOMER_FAQS_ES: FAQ[] = [
  // =============================================================================
  // Getting started
  // =============================================================================
  {
    id: 'login',
    category: 'getting_started',
    question: '¿Cómo inicio sesión?',
    tags: ['signin', 'entrar', 'acceso'],
    answer: (
      <p>
        Andá a la página de login y poné el email + contraseña que tu admin te
        invitó. Si nunca entraste antes, deberías tener un email "You've been
        invited to EzSender" — clic ahí para setear tu contraseña por primera
        vez.
      </p>
    ),
  },
  {
    id: 'forgot-password',
    category: 'getting_started',
    question: 'Olvidé mi contraseña, ¿cómo la recupero?',
    tags: ['reset', 'reseteo', 'recuperar'],
    cta: { label: 'Recuperar contraseña', to: '/forgot-password' },
    answer: (
      <p>
        En la pantalla de login hacé clic en "¿Olvidaste tu contraseña?". Te
        llega un email con un link válido por 1 hora para setear una nueva.
      </p>
    ),
  },
  {
    id: 'change-password',
    category: 'getting_started',
    question: '¿Cómo cambio mi contraseña?',
    tags: ['security', 'rotar'],
    cta: { label: 'Ir a Seguridad', to: '/settings?tab=security' },
    answer: (
      <p>
        Settings → tab <strong>Seguridad</strong> → ingresá la actual y la
        nueva. Cuando cambias la contraseña, las sesiones en otros dispositivos
        se cierran automáticamente.
      </p>
    ),
  },
  {
    id: 'invite-team',
    category: 'getting_started',
    question: '¿Cómo invito a alguien de mi equipo?',
    tags: ['team', 'usuarios', 'colaboradores'],
    cta: { label: 'Ir a Equipo', to: '/settings?tab=team' },
    answer: (
      <p>
        Settings → <strong>Equipo</strong> → "Invitar miembro". Le llega un
        email para setear su contraseña. Solo Owner y Admin pueden invitar.
      </p>
    ),
  },
  {
    id: 'roles',
    category: 'getting_started',
    question: '¿Qué hace cada rol (Owner, Admin, Operator, Viewer)?',
    tags: ['permissions', 'permisos'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Owner</strong>: dueño del workspace. Inmutable: no se puede cambiar de rol ni eliminar.</li>
        <li><strong>Admin</strong>: gestiona workspace, equipo, sender IDs, API keys, billing.</li>
        <li><strong>Operator</strong>: día a día — manda SMS, importa contactos, lanza campañas. No toca settings ni billing.</li>
        <li><strong>Viewer</strong>: solo lectura. Ve dashboards y reports pero no puede mandar nada.</li>
      </ul>
    ),
  },

  // =============================================================================
  // Senders (10DLC)
  // =============================================================================
  {
    id: 'sender-what',
    category: 'senders',
    question: '¿Qué es un Sender ID?',
    tags: ['from', 'remitente', 'numero'],
    answer: (
      <p>
        Es el número de teléfono o nombre alfanumérico desde el cual tus
        destinatarios reciben tus SMS. Antes de mandar mensajes necesitás al
        menos un Sender ID aprobado para tu cuenta.
      </p>
    ),
  },
  {
    id: 'sender-10dlc',
    category: 'senders',
    question: '¿Por qué necesito 10DLC en US?',
    tags: ['10dlc', 'a2p', 'estados unidos', 'long code'],
    answer: (
      <p>
        Los carriers de US (T-Mobile, AT&amp;T, Verizon) exigen registro 10DLC
        para todo SMS A2P (application-to-person) desde long codes. Sin el
        registro, los carriers filtran o bloquean tus mensajes. El registro
        toma 1-3 semanas y lo gestiona la plataforma.
      </p>
    ),
  },
  {
    id: 'sender-approval-time',
    category: 'senders',
    question: '¿Cuánto tarda la aprobación de un sender?',
    tags: ['10dlc', 'aprobado', 'submitted'],
    answer: (
      <p>
        Depende del tipo: <strong>long code 10DLC</strong> tarda 1-3 semanas,
        <strong> toll-free</strong> ~5 días hábiles, <strong>short code</strong> 4-6
        semanas. Mientras está en estado "submitted" o "pending" no podés
        usarlo para enviar.
      </p>
    ),
  },
  {
    id: 'sender-rejected',
    category: 'senders',
    question: 'Mi sender quedó "rejected", ¿qué hago?',
    tags: ['10dlc', 'rechazado', 'rejected'],
    answer: (
      <p>
        Significa que el carrier o el platform admin rechazó tu campaña por
        contenido / use-case. El admin va a contactarte con el motivo
        específico. Lo más común: opt-in flow no documentado o mensajes que
        suenan a SHAFT (sex / hate / alcohol / firearms / tobacco).
      </p>
    ),
  },

  // =============================================================================
  // Contactos
  // =============================================================================
  {
    id: 'contacts-import',
    category: 'contacts',
    question: '¿Cómo importo contactos desde Excel o CSV?',
    tags: ['xlsx', 'csv', 'subir', 'lista'],
    cta: { label: 'Ir a Contactos', to: '/contacts' },
    answer: (
      <p>
        Contactos → "Importar Excel/CSV" → seleccioná tu archivo. La primera
        fila debe tener nombres de columnas (los podés usar como variables en
        los mensajes). El importer detecta cuál columna es el teléfono y normaliza
        a E.164. Soporta hasta 500k filas por archivo.
      </p>
    ),
  },
  {
    id: 'contacts-variables',
    category: 'contacts',
    question: '¿Cómo uso variables en los mensajes (ej. {{Nombre}})?',
    tags: ['merge', 'mailmerge', 'personalizar'],
    answer: (
      <p>
        Los nombres de columna de tu Excel/CSV se vuelven variables. Si tu
        archivo tiene columna <code className="mono">Nombre</code>, en el
        mensaje escribís <code className="mono">{`Hola {{Nombre}}, tu pedido...`}</code>{' '}
        y se reemplaza por contacto. Si la variable no existe en una fila, se
        deja en blanco.
      </p>
    ),
  },
  {
    id: 'contacts-duplicates',
    category: 'contacts',
    question: '¿Por qué algunos contactos quedan en "duplicate" al importar?',
    tags: ['duplicado', 'dedupe'],
    answer: (
      <p>
        El importer compara el teléfono normalizado (E.164) contra los que ya
        están en la lista. Si ya existe, no lo vuelve a insertar — eso es
        intencional para evitar mandarle el mismo SMS dos veces. El resumen
        post-import te muestra cuántos quedaron así.
      </p>
    ),
  },
  {
    id: 'contacts-dnc',
    category: 'contacts',
    question: '¿Qué es la lista DNC (do-not-contact)?',
    tags: ['dnc', 'opt-out', 'stop'],
    cta: { label: 'Ver DNC', to: '/inbound' },
    answer: (
      <p>
        Cuando un destinatario responde STOP, su número entra automáticamente
        a tu lista DNC. Cualquier intento futuro de mandarle un SMS se
        rechaza con código <code className="mono">phone_in_dnc</code>. La lista
        es per-tenant — no se comparte entre customers.
      </p>
    ),
  },

  // =============================================================================
  // Enviar SMS
  // =============================================================================
  {
    id: 'send-single-vs-campaign',
    category: 'sending',
    question: '¿Cuál es la diferencia entre mensaje suelto y campaña?',
    tags: ['envio', 'masivo'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Mensaje suelto</strong>: 1 destinatario, 1 envío. Útil para tests, OTP, alertas individuales.</li>
        <li><strong>Campaña</strong>: 1 mensaje a una lista entera de contactos, con variables. Se encola en background — podés cerrar la pestaña y sigue corriendo.</li>
      </ul>
    ),
  },
  {
    id: 'send-segments',
    category: 'sending',
    question: '¿Cuántos segmentos cuesta mi mensaje?',
    tags: ['segments', 'precio', 'longitud'],
    answer: (
      <p>
        SMS se cobra por segmento, no por mensaje. <strong>GSM-7</strong> (texto
        plano sin acentos): 160 chars en 1 segmento, 153 chars/segmento si
        son varios. <strong>UCS-2</strong> (acentos / emojis / caracteres especiales):
        70 chars en 1 segmento, 67 chars/segmento si son varios. El compositor
        muestra el conteo en vivo.
      </p>
    ),
  },
  {
    id: 'send-encoding',
    category: 'sending',
    question: '¿GSM-7 vs UCS-2? ¿Cuándo se cambia?',
    tags: ['emoji', 'acentos', 'encoding', 'caracteres'],
    answer: (
      <p>
        Si todos los caracteres caben en GSM-7 (ASCII básico + algunos
        especiales tipo @, $, &amp;), tu mensaje usa GSM-7. Apenas metés
        un acento (á, ñ), un emoji 🎉, o un caracter no-ASCII, automáticamente
        cambia a UCS-2 — y los mismos chars que cabían en 160 ahora caben en 70.
        Considerar para mensajes largos.
      </p>
    ),
  },
  {
    id: 'send-schedule',
    category: 'sending',
    question: '¿Puedo programar una campaña para más tarde?',
    tags: ['scheduled', 'futuro', 'agendar'],
    answer: (
      <p>
        Sí. Cuando creás una campaña, en vez de "Lanzar ahora" elegí
        "Programar". Seteás fecha + hora + zona horaria. La campaña queda en
        estado "scheduled" y el daemon la lanza automáticamente en el momento
        elegido. Podés cancelar la programación antes de que empiece.
      </p>
    ),
  },
  {
    id: 'send-cancel',
    category: 'sending',
    question: '¿Cómo cancelo o pauso una campaña en curso?',
    tags: ['stop', 'pausar', 'parar'],
    answer: (
      <p>
        Botón "Cancelar" en la lista de campañas o en el detalle. La campaña
        deja de encolar nuevos mensajes inmediatamente. Los que ya están en la
        cola del worker pueden tardar segundos extra en parar. Mensajes ya
        enviados al carrier no se pueden recuperar.
      </p>
    ),
  },

  // =============================================================================
  // Compliance
  // =============================================================================
  {
    id: 'compliance-tcpa',
    category: 'compliance',
    question: '¿Qué es la ventana TCPA y por qué me bloquea envíos?',
    tags: ['tcpa', 'horario', 'time'],
    answer: (
      <p>
        TCPA (US) prohíbe SMS marketing fuera de 8am-9pm <strong>hora del destinatario</strong>{' '}
        (no la tuya). La plataforma chequea la zona horaria del número
        destino y bloquea envíos fuera de esa ventana — el mensaje queda en
        estado "rejected" con código <code className="mono">outside_tcpa_window</code>.
        Si querés saltearlo (transactional / 2FA), usá <code className="mono">message_type</code> diferente
        a "marketing".
      </p>
    ),
  },
  {
    id: 'compliance-stop-help',
    category: 'compliance',
    question: '¿Cómo funcionan las keywords STOP y HELP?',
    tags: ['opt-out', 'unsubscribe'],
    answer: (
      <p>
        Cuando un destinatario responde STOP (o variantes: PARAR, CANCELAR,
        UNSUBSCRIBE), la plataforma:
        {' '}<strong>(1)</strong> agrega su número a tu DNC,
        {' '}<strong>(2)</strong> manda un auto-reply de confirmación,
        {' '}<strong>(3)</strong> bloquea futuros envíos a ese número.
        HELP responde con info de contacto. Es obligatorio por TCPA — no se
        puede desactivar.
      </p>
    ),
  },
  {
    id: 'compliance-opt-in',
    category: 'compliance',
    question: '¿Necesito opt-in expreso de mis contactos?',
    tags: ['consentimiento', 'consent'],
    answer: (
      <p>
        Sí. TCPA requiere consentimiento expreso por escrito antes de mandar
        SMS marketing. Para transactional (orden enviada, OTP, alerta) basta
        un opt-in implícito por la relación de negocio. Documentá tu opt-in
        flow — los carriers lo piden al registrar 10DLC y un demanda TCPA
        puede pedírtelo.
      </p>
    ),
  },

  // =============================================================================
  // Billing
  // =============================================================================
  {
    id: 'billing-how-charged',
    category: 'billing',
    question: '¿Cómo se cobran los SMS?',
    tags: ['precio', 'costo'],
    answer: (
      <p>
        Cada segmento se cobra al precio que tu rate card define para el
        prefijo del destinatario. El cobro es <strong>antes</strong> del envío —
        si tu balance no alcanza, el mensaje queda "rejected". Si el carrier
        rechaza después del cobro (raro), automáticamente se hace refund.
      </p>
    ),
  },
  {
    id: 'billing-topup',
    category: 'billing',
    question: '¿Cómo recargo mi balance?',
    tags: ['recarga', 'pagar', 'balance'],
    cta: { label: 'Ir a Billing', to: '/billing' },
    answer: (
      <div className="space-y-2">
        <p>
          Billing → <strong>Top up</strong> (o el botón del widget de balance). Elegí un monto y después un método de pago:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Tarjeta</strong> — al instante, por el checkout seguro de Stripe. Tu balance se actualiza solo apenas se acredita el pago.</li>
          <li><strong>PayPal, Zelle, Crypto (USDT) o Wire / ACH</strong> — el panel muestra los datos exactos de pago. Después de pagar, enviá tu comprobante y acreditamos el balance manualmente.</li>
        </ul>
        <p>
          Cada recarga aparece como un <code className="mono">deposit</code> en tu historial de transacciones.
        </p>
      </div>
    ),
  },
  {
    id: 'billing-low-balance',
    category: 'billing',
    question: '¿Qué pasa cuando mi balance baja?',
    tags: ['alerta', 'aviso'],
    answer: (
      <p>
        Cuando el balance baja del umbral (default $10), recibís un email +
        un banner en la app. Si llega a $0, los nuevos envíos se rechazan
        con <code className="mono">insufficient_balance</code> hasta que
        recargues. Los envíos en cola que ya cobraron se completan normal.
      </p>
    ),
  },
  {
    id: 'billing-plan',
    category: 'billing',
    question: '¿Qué plan tengo y qué incluye?',
    tags: ['suscripcion', 'subscription'],
    cta: { label: 'Ver mi plan', to: '/billing' },
    answer: (
      <p>
        En Billing podés ver tu plan actual, fee mensual (si aplica), SMS
        incluidos y costo por overage. Los planes los crea tu reseller — si
        querés cambiar, contactalo directo.
      </p>
    ),
  },

  // =============================================================================
  // Reports
  // =============================================================================
  {
    id: 'reports-statuses',
    category: 'reports',
    question: '¿Qué significa cada status (queued / sent / delivered / failed)?',
    tags: ['estado', 'status', 'mensaje'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>queued</strong>: aceptado, esperando turno en el worker.</li>
        <li><strong>sent</strong>: el carrier (Twilio/etc) lo aceptó. Aún no confirmó delivery.</li>
        <li><strong>delivered</strong>: el carrier confirmó entrega al teléfono.</li>
        <li><strong>failed</strong>: rechazado en algún paso. El campo error_code te dice por qué.</li>
        <li><strong>rejected</strong>: la plataforma lo bloqueó (TCPA, DNC, balance, sender no aprobado, etc) antes de mandarlo al carrier.</li>
      </ul>
    ),
  },
  {
    id: 'reports-delivery-rate',
    category: 'reports',
    question: '¿Cómo veo mi tasa de delivery por país?',
    tags: ['kpi', 'rate', 'metricas'],
    cta: { label: 'Ir a Reports', to: '/reports' },
    answer: (
      <p>
        Reports → seleccioná el rango de fechas → mirá el bloque "Top
        países por volumen" + las KPIs arriba (Total / Delivered / Failed).
        El % de delivery se calcula como delivered / (delivered + failed),
        excluyendo los rejected.
      </p>
    ),
  },
  {
    id: 'reports-export',
    category: 'reports',
    question: '¿Puedo exportar mis mensajes a CSV?',
    tags: ['csv', 'descargar', 'export'],
    answer: (
      <p>
        Sí. Reports → "Exportar CSV" exporta todos los mensajes del rango
        elegido. También en cada campaña detail hay un "Exportar" para los
        mensajes de esa campaña específica. CSVs incluyen todos los campos
        de status + error code para análisis externo.
      </p>
    ),
  },

  // =============================================================================
  // Troubleshooting
  // =============================================================================
  {
    id: 'trouble-sms-rejected',
    category: 'troubleshooting',
    question: 'Mi SMS quedó "rejected", ¿por qué?',
    tags: ['error', 'rechazado'],
    answer: (
      <p>
        Mirá la columna error_code en Messages. Los más comunes:
        {' '}<code className="mono">sender_not_approved</code> (10DLC pendiente),
        {' '}<code className="mono">outside_tcpa_window</code> (fuera de 8am-9pm),
        {' '}<code className="mono">phone_in_dnc</code> (destinatario hizo opt-out),
        {' '}<code className="mono">insufficient_balance</code> (sin saldo),
        {' '}<code className="mono">no_route_available</code> (no tenés rate card para ese país).
      </p>
    ),
  },
  {
    id: 'trouble-no-emails',
    category: 'troubleshooting',
    question: 'No me llegan los emails de la plataforma',
    tags: ['email', 'spam', 'bandeja'],
    answer: (
      <p>
        Chequeá <strong>Spam / Promociones</strong> primero. Si no está ahí,
        confirma que tu email es el correcto en Settings → Workspace. Los
        emails vienen desde <code className="mono">noreply@getezsender.com</code> —
        agregalo a tus contactos para evitar filtros futuros. Si igual no llegan,
        contactá a tu admin.
      </p>
    ),
  },
  {
    id: 'trouble-cant-login',
    category: 'troubleshooting',
    question: 'No puedo iniciar sesión aunque la contraseña es correcta',
    tags: ['login', 'acceso', '401'],
    answer: (
      <p>
        Tres causas comunes: <strong>(1)</strong> CAPS LOCK activado,
        {' '}<strong>(2)</strong> tu password manager está autollenando una
        password vieja — probá en modo incógnito y tipeala manual,
        {' '}<strong>(3)</strong> tu cuenta está desactivada (preguntá al admin).
        Si todo lo anterior está OK, "¿Olvidaste tu contraseña?" reseteala.
      </p>
    ),
  },
  {
    id: 'trouble-suspended',
    category: 'troubleshooting',
    question: 'Mi cuenta dice "suspended" — ¿qué pasó?',
    tags: ['suspendido', 'bloqueado'],
    answer: (
      <p>
        Tu reseller suspendió la cuenta — generalmente por balance vencido
        o violación de policy. Contactá a tu account manager. Los envíos
        quedan bloqueados pero los datos (contactos, historial, settings)
        están intactos.
      </p>
    ),
  },
  {
    id: 'trouble-api-keys',
    category: 'troubleshooting',
    question: '¿Cómo creo una API key para integrar mi backend?',
    tags: ['api', 'integration', 'key'],
    cta: { label: 'Ir a Developers', to: '/developers' },
    answer: (
      <p>
        Developers → "New API key" → la creás con nombre descriptivo. Solo
        Admin+ puede crearlas. <strong>Importante:</strong> el secret completo se
        muestra UNA SOLA VEZ al crear. Copialo a tu password manager
        inmediatamente — después solo verás los últimos 4 chars.
      </p>
    ),
  },
  {
    id: 'trouble-webhooks',
    category: 'troubleshooting',
    question: '¿Cómo recibo webhooks cuando un SMS se entrega?',
    tags: ['webhook', 'callback', 'integracion'],
    cta: { label: 'Ir a Webhooks', to: '/webhooks' },
    answer: (
      <p>
        Webhooks → "New endpoint" → URL pública tuya + eventos a suscribir
        (delivery, failure, inbound, etc). Cada POST viene firmado HMAC-SHA256
        para que verifiques que vino de nosotros. Reintentos automáticos con
        backoff exponencial si tu endpoint da 5xx.
      </p>
    ),
  },

  // =============================================================================
  // Voz — campañas salientes (Press-1 / IVR broadcast)
  // =============================================================================
  {
    id: 'voice-what',
    category: 'voice',
    question: '¿Qué puedo hacer con el módulo de Voz?',
    tags: ['voz', 'press-1', 'ivr', 'broadcast', 'telefono'],
    cta: { label: 'Abrir Voice campaigns', to: '/voice/campaigns' },
    answer: (
      <div className="space-y-2">
        <p>
          Voz te permite hacer dos tipos de trabajos por llamada desde EzSender:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Broadcasts salientes Press-1 / IVR</strong> — tu lista de contactos recibe una llamada que reproduce un saludo, captura una tecla (ej. "Marcá 1 para hablar con un agente") y rutea según corresponda.</li>
          <li><strong>IVR entrante</strong> — los clientes llaman a tu DID (tu número de teléfono comercial) y tu flow responde — menú, chequeo de horario, voicemail, agente IA, etc.</li>
        </ul>
        <p>
          Ambos comparten los mismos bloques (flows, audio assets, DIDs, rate cards) así que podés combinar.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-flow-builder',
    category: 'voice',
    question: '¿Cómo armo un flow IVR?',
    tags: ['ivr', 'flow', 'canvas', 'builder', 'press-1'],
    cta: { label: 'Abrir Flows', to: '/voice/flows' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>Flows</strong> → "New flow". Al inicio elegís inbound u outbound — eso define qué paleta de nodos te muestra el canvas.
        </p>
        <p>
          Arrastrá nodos al canvas (Play audio, Press para capturar dígitos, Transfer, AI Agent, Voicemail, etc.) y conectalos. Click en cualquier nodo edita sus settings en el Inspector de la derecha.
        </p>
        <p>
          Guardar crea una nueva versión. Cada save bumpea el número de versión así que podés rollbackear. Activá <strong>Use v2-native runtime</strong> en settings para desbloquear los nodos avanzados (Webhook, Condition, Record, TTS dinámico con variables <code>{`{{contact.X}}`}</code>).
        </p>
      </div>
    ),
  },
  {
    id: 'voice-dids',
    category: 'voice',
    question: '¿Qué es un DID y cómo agrego uno?',
    tags: ['did', 'caller-id', 'numero', 'telefono'],
    cta: { label: 'Abrir DIDs', to: '/voice/dids' },
    answer: (
      <div className="space-y-2">
        <p>
          Un DID es un número de teléfono que vos tenés — se usa como <strong>caller ID</strong> en campañas salientes y como <strong>número entrante</strong> al que pueden llamar tus clientes para llegar a tu IVR.
        </p>
        <p>
          IVR → <strong>DIDs</strong> → "Add DID" e ingresá el número en formato E.164 (+15551234567). Vos podés registrar el número, pero solo el operador del platform puede marcarlo <strong>verified</strong> — eso requiere LOA / confirmación del carrier.
        </p>
        <p>
          Hasta que un DID esté verified+active, las campañas y flows inbound no lo pueden usar.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-audio-assets',
    category: 'voice',
    question: '¿Cómo agrego saludos / prompts de audio?',
    tags: ['audio', 'wav', 'mp3', 'tts', 'saludo'],
    cta: { label: 'Abrir Audio Assets', to: '/voice/assets' },
    answer: (
      <div className="space-y-2">
        <p>
          Dos formas:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Upload</strong> de un archivo WAV o MP3 desde IVR → Audio Assets. Mejor para grabaciones con marca.</li>
          <li><strong>TTS</strong> (texto a voz) — escribís el texto + elegís una voz, nosotros generamos el audio. Soporta variables tipo <code>{`{{contact.first_name}}`}</code> cuando el flow usa el runtime v2-native.</li>
        </ul>
        <p>
          Una vez subido/generado, el asset está disponible para usar en cualquier nodo Play en tu editor.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-launch-campaign',
    category: 'voice',
    question: '¿Cómo lanzo una campaña de voz?',
    tags: ['campana', 'launch', 'broadcast', 'press-1'],
    cta: { label: 'Abrir Campaigns', to: '/voice/campaigns' },
    answer: (
      <div className="space-y-2">
        <p>
          Voice → <strong>Campaigns</strong> → "New campaign". Elegí un flow, una lista de contactos, un caller ID (un DID fijo, o un pool de rotación con local presence — mirá <em>Caller ID pools</em>), el pacing (calls/minuto), y opcionalmente activá AMD. Guardá como draft.
        </p>
        <p>
          Desde el detail page click <strong>Launch now</strong> o programá para más tarde. Antes de lanzar, usá <strong>Test call</strong> para llamarte a vos mismo y verificar el flow end-to-end.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-caller-id-pools',
    category: 'voice',
    question: '¿Qué son los Caller ID pools y el local presence?',
    tags: ['caller-id', 'pool', 'local presence', 'rotacion', 'area code'],
    cta: { label: 'Abrir Caller ID pools', to: '/voice/caller-id-pools' },
    answer: (
      <div className="space-y-2">
        <p>
          Un Caller ID pool es un grupo de tus números que la campaña rota — un caller ID distinto por llamada — en vez de mostrar siempre un número fijo. Creá uno en Voice → <strong>Caller ID pools</strong> y pegá o importá tus números.
        </p>
        <p>Al armar la campaña elegís una <strong>estrategia de Caller ID</strong>:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Fijo</strong> — siempre presenta un DID que elegís.</li>
          <li><strong>Aleatorio</strong> — un número al azar del pool en cada llamada.</li>
          <li><strong>Local presence</strong> — prefiere un número del pool del mismo área code del destino, luego su estado, luego aleatorio. Mostrar un número local sube la tasa de contestación.</li>
        </ul>
        <p>
          Con una estrategia de pool no necesitás elegir un DID fijo — el pool provee el número automáticamente.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-amd',
    category: 'voice',
    question: '¿Qué es AMD (detección de contestadora)?',
    tags: ['amd', 'voicemail', 'contestadora', 'deteccion'],
    cta: { label: 'Abrir Campaigns', to: '/voice/campaigns' },
    answer: (
      <div className="space-y-2">
        <p>
          AMD intenta distinguir una persona en vivo de un voicemail / contestadora apenas se contesta la llamada. Se activa por campaña en la sección <strong>Answering machine</strong>. Cuando detecta una máquina, elegís qué pasa:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Continuar el flow</strong> (default) — el saludo se reproduce igual y AMD solo lo anota en el reporte. La opción más segura.</li>
          <li><strong>Colgar</strong> — corta la llamada para ahorrar en máquinas.</li>
          <li><strong>Dejar voicemail y colgar</strong> — espera el beep, reproduce un mensaje pre-grabado y cuelga.</li>
        </ul>
        <p>
          La detección es aproximada — con audio tardío del carrier a veces confunde a una persona real, por eso <em>Continuar</em> es el default. El resultado queda guardado en cada llamada para tus reportes.
        </p>
      </div>
    ),
  },

  // =============================================================================
  // AI Agents
  // =============================================================================
  {
    id: 'ai-agent-what',
    category: 'ai_agents',
    question: '¿Qué es un AI Agent?',
    tags: ['ai', 'agent', 'voz', 'gpt', 'claude'],
    cta: { label: 'Abrir AI Agents', to: '/voice/ai-agents' },
    answer: (
      <div className="space-y-2">
        <p>
          Un AI Agent es un asistente de voz conversacional — cuando una llamada llega a un nodo <code>ai_agent</code> en tu flow inbound (o lo embebés en una campaña outbound), la IA escucha, entiende, responde en lenguaje natural, y puede ejecutar acciones (agendar citas, buscar info de clientes, transferir a un humano, etc).
        </p>
        <p>
          Entrenás al agente sin código: describís el negocio, la identidad del agente, qué debe y qué no debe decir, los idiomas que habla. Nosotros compilamos eso en un system prompt para el LLM subyacente (Claude Haiku por default).
        </p>
      </div>
    ),
  },
  {
    id: 'ai-agent-create',
    category: 'ai_agents',
    question: '¿Cómo creo un AI Agent para mi negocio?',
    tags: ['ai', 'agent', 'crear', 'profile'],
    cta: { label: 'Abrir AI Agents', to: '/voice/ai-agents' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>AI Agents</strong> → "New profile". Llená:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Identidad</strong>: nombre del agente, idiomas, persona.</li>
          <li><strong>Negocio</strong>: nombre, qué vendés, horarios, ubicación.</li>
          <li><strong>Conocimiento</strong>: pegá snippets de FAQ o usá el URL ingester para traer contenido de tu web actual.</li>
          <li><strong>Comportamiento</strong>: cosas que el agente nunca debe decir, reglas de escalación.</li>
        </ul>
        <p>
          Guardá. Después en el flow editor agregá un nodo <code>ai_agent</code> y elegí este profile en el Inspector.
        </p>
      </div>
    ),
  },
  {
    id: 'ai-agent-test',
    category: 'ai_agents',
    question: '¿Cómo pruebo el AI Agent sin llamar de verdad?',
    tags: ['ai', 'agent', 'test', 'simulador'],
    answer: (
      <p>
        Abrí el profile del AI Agent y click en <strong>Test call</strong>. Marcamos un número que especifiques (el tuyo) y te conectamos con el agente para una conversación real. Cada test call cuesta lo mismo que una producción — mantenelos cortos.
      </p>
    ),
  },
  {
    id: 'ai-agent-languages',
    category: 'ai_agents',
    question: '¿El agente habla varios idiomas?',
    tags: ['ai', 'idioma', 'es', 'en', 'espanol', 'ingles'],
    answer: (
      <div className="space-y-2">
        <p>
          Sí. En la sección Identidad del profile elegí uno o más idiomas. Si seleccionás solo uno (ej. <code>es-MX</code>), el agente queda bloqueado a ese idioma — útil para negocios donde mezclar idiomas no queda bien.
        </p>
        <p>
          Con varios idiomas el agente detecta el idioma del caller y se adapta.
        </p>
      </div>
    ),
  },

  // =============================================================================
  // IVR entrante — DIDs, horarios, voicemails, recordings, webhook secret
  // =============================================================================
  {
    id: 'inbound-routing',
    category: 'inbound',
    question: '¿Cómo llegan las llamadas a mi flow?',
    tags: ['inbound', 'did', 'routing'],
    cta: { label: 'Abrir DIDs', to: '/voice/dids' },
    answer: (
      <div className="space-y-2">
        <p>
          Para cada DID, configurás qué pasa cuando alguien lo llama: abrí la fila del DID, click <strong>Inbound config</strong>, y elegí un tipo de ruteo:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Flow</strong> — tu IVR flow responde. Lo más común.</li>
          <li><strong>AI Agent</strong> — directo a un profile de AI Agent (sin menú IVR).</li>
          <li><strong>Transfer</strong> — reenvía la llamada a otro número o SIP URI.</li>
          <li><strong>Voicemail</strong> — siempre a voicemail.</li>
        </ul>
        <p>
          También podés wirear un fallback para horario fuera-de-oficina y un número separado para recibir notificaciones SMS cuando dejan voicemail.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-business-hours',
    category: 'inbound',
    question: '¿Cómo seteo el horario de atención?',
    tags: ['horario', 'schedule', 'business-hours'],
    cta: { label: 'Abrir Business hours', to: '/voice/business-hours' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>Business hours</strong> → toggle cada día abierto/cerrado y elegí start + end times. Se guarda como el default workspace-wide.
        </p>
        <p>
          Los flows inbound pueden usar el nodo <code>schedule_check</code> (en flows v2-native) para branchear según si la llamada está dentro o fuera de horario. Cuando cerrado, el default es voicemail si el DID lo tiene activado, sino la llamada cuelga limpiamente.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-voicemails',
    category: 'inbound',
    question: '¿Dónde se guardan los voicemails?',
    tags: ['voicemail', 'fuera-horario', 'inbox'],
    cta: { label: 'Abrir Voicemails', to: '/voice/voicemails' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>Voicemails</strong>. Cada fila tiene el número del caller, cuándo llamó, la grabación, y (si tu plan tiene transcripción) un transcript en texto.
        </p>
        <p>
          Filtrá por All / Unread. Click en una fila reproduce el audio inline y la marca como leída.
        </p>
        <p>
          También podés recibir una notificación SMS cuando entra un voicemail — configurá el destino en la Inbound config del DID.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-recordings',
    category: 'inbound',
    question: '¿Cuál es la diferencia entre Voicemails y Recordings?',
    tags: ['voicemail', 'recording', 'capture'],
    answer: (
      <div className="space-y-2">
        <p>
          <strong>Voicemails</strong> — capturas hechas cuando el caller no pudo llegar a un humano (cerrado, sin respuesta, ruteo solo-voicemail). Una por llamada.
        </p>
        <p>
          <strong>Recordings</strong> — capturas hechas por un nodo <code>record</code> dentro de tu flow IVR. Ej: "Por favor dictá tu número de cuenta después del bip" — ese audio aparece en <strong>Recordings</strong>, agrupado por flow y nodo que capturó. Útil para formularios por voz.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-webhook-secret',
    category: 'inbound',
    question: '¿Cómo firmo webhooks salientes desde mi IVR?',
    tags: ['webhook', 'signing', 'hmac', 'seguridad'],
    cta: { label: 'Abrir Webhook secret', to: '/voice/webhook-secret' },
    answer: (
      <div className="space-y-2">
        <p>
          Cuando tu flow IVR usa un nodo <code>webhook</code> para llamar a tu CRM, podés firmar cada request para que el CRM verifique que vino realmente de nosotros.
        </p>
        <p>
          IVR → <strong>Webhook secret</strong> → "Rotate secret". El secret completo se muestra <strong>una sola vez</strong> — copialo a la config del verifier de tu CRM. Rotaciones futuras invalidan el secret anterior.
        </p>
        <p>
          Formato de firma: <code>X-EzSender-Signature: t=&lt;unix&gt;,v1=&lt;hmac-sha256(secret, ts.body)&gt;</code> (patrón Stripe).
        </p>
      </div>
    ),
  },
];
